import { useEffect, useId } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle2, ChevronDown, CircleX, Wrench } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";
import { CreatedTaskPill } from "@/components/ai-elements/created-task-pill";
import type { HistoricalToolDetailsState } from "@/hooks/use-tool-details";
import type { CreatedTaskReference } from "@/lib/created-task";
import type {
  ToolDetailField,
  ToolDetails,
  ToolDetailSource,
} from "@/lib/tool-details";
import { cn } from "@/lib/utils";
import { workflowTaskIdFromToolResult } from "@/lib/workflow-run-links";

const MAX_DISPLAY_CHARS = 16_000;

export interface ToolCallBadgeProps {
  id: string;
  status: "start" | "end" | "error";
  name: string;
  source: ToolDetailSource;
  open: boolean;
  historicalState: HistoricalToolDetailsState;
  onOpenChange: (open: boolean) => void;
  onRetry: () => void;
  onPreloadWorkflowRun?: (source: ToolDetailSource, name: string) => void;
  /** Server-derived ID on canonical workflow launch rows; absent on older servers. */
  workflowTaskId?: unknown;
  /** Task produced by this completed `create_task` call, when safely resolved. */
  createdTask?: CreatedTaskReference | null;
  onOpenRun?: (taskId: string) => void;
}

interface DisplayValue {
  text: string;
  truncated: boolean;
}

function formatValue(value: unknown): DisplayValue {
  let displayValue = value;
  if (typeof displayValue === "string") {
    const trimmedValue = displayValue.trim();
    if (trimmedValue.startsWith("{") || trimmedValue.startsWith("[")) {
      try {
        displayValue = JSON.parse(displayValue);
      } catch {
        displayValue = value;
      }
    }
  }

  let text: string;
  try {
    const serialized = JSON.stringify(displayValue, null, 2);
    text = serialized === undefined ? String(displayValue) : serialized;
  } catch {
    text = String(displayValue);
  }
  if (text.length <= MAX_DISPLAY_CHARS) return { text, truncated: false };
  return {
    text: `${text.slice(0, MAX_DISPLAY_CHARS)}\n…`,
    truncated: true,
  };
}

function FieldValue({ field }: { field: ToolDetailField }) {
  const { t } = useTranslation("chat");
  if (field.state !== "available") {
    return <p className="text-xs text-muted-foreground">{t(`tool.${field.state}`)}</p>;
  }
  const formatted = formatValue(field.value);
  return (
    <>
      <pre className="max-h-48 overflow-auto rounded-md border bg-background/70 p-2 text-xs leading-5 text-foreground">
        <code className="whitespace-pre-wrap break-all font-mono">{formatted.text}</code>
      </pre>
      {formatted.truncated && (
        <p className="mt-1 text-xs text-muted-foreground">{t("tool.truncated")}</p>
      )}
    </>
  );
}

function DetailSections({
  status,
  details,
  live,
}: {
  status: ToolCallBadgeProps["status"];
  details: ToolDetails;
  live: boolean;
}) {
  const { t } = useTranslation("chat");
  const terminalField = status === "error" ? details.error : details.result;
  const terminalLabel = status === "error" ? t("tool.error") : t("tool.result");

  return (
    <div className="space-y-2">
      <section aria-label={t("tool.parameters")}>
        <p className="mb-1 text-xs font-medium text-muted-foreground">{t("tool.parameters")}</p>
        <FieldValue field={details.parameters} />
      </section>
      {live && status === "start" ? (
        <p className="text-xs text-muted-foreground">{t("tool.awaitingResult")}</p>
      ) : (
        <>
          <Separator />
          <section aria-label={terminalLabel}>
            <p className="mb-1 text-xs font-medium text-muted-foreground">{terminalLabel}</p>
            <FieldValue field={terminalField} />
          </section>
        </>
      )}
    </div>
  );
}

export function ToolCallBadge({
  id,
  status,
  name,
  source,
  open,
  historicalState,
  onOpenChange,
  onRetry,
  onPreloadWorkflowRun,
  workflowTaskId: persistedWorkflowTaskId,
  createdTask,
  onOpenRun,
}: ToolCallBadgeProps) {
  const { t } = useTranslation("chat");
  const generatedId = useId();
  const regionId = `tool-details-${id}-${generatedId}`.replace(/[^A-Za-z0-9_-]/g, "");
  const isRunning = status === "start";
  const isError = status === "error";
  const live = source.kind === "live";
  const details = live
    ? source.details
    : historicalState.status === "ready"
      ? historicalState.data
      : null;
  const workflowTaskId = workflowTaskIdFromToolResult(name, status, details?.result, persistedWorkflowTaskId);

  useEffect(() => {
    if (source.kind !== "history" || workflowTaskId || (name !== "run_workflow" && name !== "run_workflow_direct")) return;
    // Let the parent resolver's session-reset effect run before requesting.
    let active = true;
    queueMicrotask(() => {
      if (active) onPreloadWorkflowRun?.(source, name);
    });
    return () => { active = false; };
  }, [source, name, workflowTaskId, onPreloadWorkflowRun]);

  const badgeVariant = isError ? "destructive" : isRunning ? "outline" : "secondary";
  const StatusIcon = isError ? CircleX : isRunning ? Wrench : CheckCircle2;

  return (
    <div className="w-fit max-w-full">
      <Collapsible open={open} onOpenChange={onOpenChange} className="my-1.5 w-fit max-w-full">
        <CollapsibleTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-expanded={open}
            aria-controls={regionId}
            aria-label={t(open ? "tool.collapse" : "tool.expand", {
              name,
              state: isRunning ? t("tool.running") : isError ? t("tool.failed") : t("tool.completed"),
            })}
            className="h-7 max-w-full gap-1.5 px-1.5 text-xs hover:bg-muted focus-visible:ring-2"
          >
            <Badge
              asChild
              variant={badgeVariant}
              className="max-w-[18rem] gap-1 py-0.5 font-medium"
            >
              <span>
                <StatusIcon aria-hidden="true" className="size-3 shrink-0" />
                <span className="truncate">{name}</span>
              </span>
            </Badge>
            {isRunning && <Spinner className="size-3" aria-label={t("tool.running")} />}
            <ChevronDown
              aria-hidden="true"
              className={cn(
                "size-3 text-muted-foreground transition-transform",
                open && "rotate-180",
              )}
            />
          </Button>
        </CollapsibleTrigger>

        {open && (
          <CollapsibleContent
            id={regionId}
            role="region"
            aria-label={t("tool.details")}
            className="pt-1.5"
          >
            <Card className="w-[min(32rem,calc(100vw-3rem))] bg-muted/35 p-2.5 shadow-none">
              {live && details ? (
                <DetailSections status={status} details={details} live />
              ) : source.kind === "history" && historicalState.status === "loading" ? (
                <div aria-live="polite" className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Spinner className="size-3" />
                  <span>{t("tool.loading")}</span>
                </div>
              ) : source.kind === "history" && historicalState.status === "fetch_error" ? (
                <Alert variant="destructive" className="p-2 text-xs" aria-live="polite">
                  <AlertDescription className="flex items-center justify-between gap-2">
                    <span>{t("tool.fetchFailed")}</span>
                    <Button type="button" size="sm" variant="outline" onClick={onRetry}>
                      {t("tool.retry")}
                    </Button>
                  </AlertDescription>
                </Alert>
              ) : details ? (
                <DetailSections status={status} details={details} live={false} />
              ) : (
                <p className="text-xs text-muted-foreground">{t("tool.unavailable")}</p>
              )}
            </Card>
          </CollapsibleContent>
        )}
      </Collapsible>
      {createdTask && (
        <CreatedTaskPill task={createdTask} className="mb-1.5 ml-1.5 mt-0.5" />
      )}
      {status === "end" && (name === "run_workflow" || name === "run_workflow_direct") && (
        <CreatedTaskPill kind="workflow-run" taskId={workflowTaskId}
          onClick={workflowTaskId && onOpenRun ? () => onOpenRun(workflowTaskId) : undefined}
          className="mb-1.5 ml-1.5 mt-0.5" />
      )}
    </div>
  );
}
